import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import cn from "classnames";
import { format, isSameDay, parse, isValid } from "date-fns";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";

import {
  attachDateMask,
  DateMaskConfig,
  formatDateMaskValue,
} from "@packages/shared/utils/dateMask";

import { BasicInput, BasicInputProps } from "@/components/BasicInput";
import { useDropdown } from "@/hooks/useDropdown";

import { Calendar } from "./Calendar";

import styles from "./DatePicker.module.scss";

const dateToString = (date: Value) => {
  return date ? format(date, "dd/MM/yyyy") : "";
};

type Value = Date | undefined;

const DATE_MASK: DateMaskConfig = {
  mask: "99.99.9999",
  placeholder: "dd.mm.yyyy",
};

export interface DateInputProps
  extends Omit<BasicInputProps, "value" | "onChange"> {
  type: "date";
  value: Value;
  onChange: (value: Value) => void;
}

export const DateInput = observer(
  forwardRef<HTMLInputElement, DateInputProps>(function DateInput(props, ref) {
    const {
      containerClassName,
      value,
      onChange,
      placeholder = "",
      error,
      variant = "primary",
      disabled,
    } = props;

    const containerClasses = cn(styles.container, containerClassName, {
      [styles.error]: !!error,
    });

    const { t } = useTranslation();

    const inputRef = useRef<HTMLInputElement | null>(null);

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const {
      containerRef: dropdownContainerRef,
      isOpen: isDropdownOpen,
      open: showDropdown,
      styles: dropdownStyles,
      close: closeDropdown,
    } = useDropdown<HTMLDivElement>();

    const [innerValue, setInnerValue] = useState(() => dateToString(value));
    // Этот стейт нужен что-бы ничего не показывать в календаре пока в инпуте не полная дата
    const [innerDate, setInnerDate] = useState<Date | null>(
      value ?? new Date()
    );

    // onInput, а не onChange: маска меняет значение программно (см. attachDateMask)
    const handleInput = (event: React.FormEvent<HTMLInputElement>) => {
      const dateRegex = /\d{2}\.\d{2}\.\d{4}/;
      const { value: inputValue } = event.currentTarget;

      setInnerValue(inputValue);

      // Вызов верхнего события только когда дата введена полностью
      if (dateRegex.test(inputValue)) {
        const newValue = parse(inputValue, "dd.MM.yyyy", new Date());
        if (isValid(newValue)) {
          onChange(newValue);
        }
      } else {
        onChange(undefined);
      }
    };

    const handleCalendarChange = (date: Date, shouldClose = false) => {
      onChange(date);
      if (shouldClose) {
        closeDropdown();
      }
    };

    const handleClear = () => {
      onChange(undefined);
      setInnerValue("");
      setInnerDate(null);
      closeDropdown();
    };

    const handleClickToday = () => {
      onChange(new Date());
      closeDropdown();
    };

    useLayoutEffect(() => {
      if (value === innerDate || value === undefined) {
        return;
      }

      if (
        (value === null && innerDate !== null) ||
        (value !== null && innerDate === null) ||
        !isSameDay(value as Date, innerDate as Date)
      ) {
        setInnerValue(dateToString(value));
        setInnerDate(value);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    useEffect(() => {
      if (!inputRef.current) {
        return;
      }

      return attachDateMask(inputRef.current, DATE_MASK);
    }, []);

    return (
      <div ref={dropdownContainerRef} className={containerClasses}>
        <BasicInput
          ref={inputRef}
          variant={variant}
          error={error}
          value={
            innerValue ? formatDateMaskValue(innerValue, DATE_MASK) : innerValue
          }
          onInput={handleInput}
          placeholder={placeholder}
          onClick={showDropdown}
          onFocus={showDropdown}
          disabled={disabled}
        />

        {isDropdownOpen && (
          <div
            className={cn(styles["dropdown"], styles[`dropdown-${variant}`])}
            style={dropdownStyles}
          >
            <Calendar
              value={value ?? new Date()}
              onChange={handleCalendarChange}
            />
            <div className={styles["calendar-buttons"]}>
              <button
                className={styles["calendar-button"]}
                onClick={handleClickToday}
              >
                {t("DatePicker:Today")}
              </button>
              <button
                className={styles["calendar-button"]}
                onClick={handleClear}
              >
                {t("DatePicker:Clear")}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  })
);
